import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, RouterProvider, createRootRoute, createRoute, createRouter, } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { api } from './api.js';
import { CloseReadingPage } from './routes/CloseReadingPage.js';
import { ComparePage } from './routes/ComparePage.js';
import { ExamsPage } from './routes/ExamsPage.js';
import { LoginPage } from './routes/LoginPage.js';
import { MistakesPage } from './routes/MistakesPage.js';
import { PracticePage } from './routes/PracticePage.js';
import { RecordsPage } from './routes/RecordsPage.js';
import { ReviewPage } from './routes/ReviewPage.js';
import { useTheme } from './theme.js';
function ShellSkeleton() {
    return (_jsxs("div", { className: "page", children: [_jsx("div", { className: "skeleton", style: { height: 42, width: 260, marginBottom: 24 } }), _jsx("div", { className: "exam-grid", children: [0, 1, 2, 3].map((index) => (_jsx("div", { className: "skeleton", style: { height: 168 } }, index))) })] }));
}
function RootLayout() {
    const queryClient = useQueryClient();
    const { theme, toggle } = useTheme();
    const session = useQuery({ queryKey: ['me'], queryFn: api.me, retry: false });
    if (session.isPending) {
        return (_jsx("div", { className: "app", children: _jsx(ShellSkeleton, {}) }));
    }
    if (!session.data) {
        return _jsx(LoginPage, { onLoggedIn: () => void queryClient.invalidateQueries({ queryKey: ['me'] }) });
    }
    return (_jsxs("div", { className: "app", children: [_jsx("header", { className: "topbar", children: _jsxs("div", { className: "topbar__inner", children: [_jsxs("span", { className: "brand", children: ["LEITS", _jsx("span", { className: "brand__sub", children: "\u9605\u8BFB\u7EC3\u4E60" })] }), _jsxs("nav", { className: "nav", "aria-label": "\u4E3B\u5BFC\u822A", children: [_jsx(Link, { to: "/", className: "nav__link", activeProps: { className: 'nav__link is-active' }, activeOptions: { exact: true }, children: "\u9898\u5E93" }), _jsx(Link, { to: "/records", className: "nav__link", activeProps: { className: 'nav__link is-active' }, children: "\u8BB0\u5F55" }), _jsx(Link, { to: "/mistakes", className: "nav__link", activeProps: { className: 'nav__link is-active' }, children: "\u9519\u9898\u672C" })] }), _jsx("span", { className: "faint", children: session.data.username }), _jsx("button", { type: "button", className: "btn btn--ghost btn--sm", onClick: toggle, children: theme === 'dark' ? '浅色' : '深色' }), _jsx("button", { type: "button", className: "btn btn--ghost btn--sm", onClick: async () => {
                                await api.logout();
                                queryClient.clear();
                                void queryClient.invalidateQueries({ queryKey: ['me'] });
                            }, children: "\u9000\u51FA" })] }) }), _jsx(Outlet, {})] }));
}
const rootRoute = createRootRoute({ component: RootLayout });
const examsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    component: ExamsPage,
});
const recordsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/records',
    component: RecordsPage,
});
const mistakesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/mistakes',
    component: MistakesPage,
});
const practiceRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/practice/$examId',
    component: PracticePage,
});
const reviewRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/review/$attemptId',
    component: ReviewPage,
});
const closeReadingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/close-reading/$examId',
    component: CloseReadingPage,
});
const compareRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/compare/$examId',
    component: ComparePage,
});
const routeTree = rootRoute.addChildren([
    examsRoute,
    recordsRoute,
    mistakesRoute,
    practiceRoute,
    reviewRoute,
    closeReadingRoute,
    compareRoute,
]);
const router = createRouter({ routeTree, defaultPreload: 'intent' });
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1,
            refetchOnWindowFocus: false,
            staleTime: 10_000,
        },
    },
});
const container = document.getElementById('root');
if (!container)
    throw new Error('缺少 #root 容器');
createRoot(container).render(_jsx(StrictMode, { children: _jsx(QueryClientProvider, { client: queryClient, children: _jsx(RouterProvider, { router: router }) }) }));
//# sourceMappingURL=main.js.map